/**
 * The call behind what is about to be accepted — drawn beside every Accept
 * (readout item 3, 2026-11-01; the rule of #1358 and #1364: a standing is
 * never drawn alone, canon 02B L1).
 *
 * A packet carries a ranked list: the call is the decision that produced that
 * order, read by the session the packet names — the session's own publication
 * or, since migration 1008, the Decision of the list the session sent up. An
 * item proposal asks to change a standing: the call is the decision behind the
 * item's most authoritative rank today. Where the record holds no decision the
 * screen says so, never a guess. Nothing here blocks the act.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useDecisionsStore } from '../../../stores/prioritization/decisionsStore';
import { CallSentence } from '../decisions/CallSentence';
import {
  decisionForIncomingList,
  decisionForRanking,
  incomingListBasisText,
  incomingListGapText,
  pickRankingForTrail,
  rankingDecisionGapText,
} from '../../../lib/call/rankingDecision';
import { fetchListApprovalDecisionIds } from '../../../lib/call/rankingDecisionService';
import { fetchItemRankings } from '../../../lib/itemRankingsService';
import type { ItemRanking } from '../../../lib/itemRankings';

export interface CallBehindProposalProps {
  proposal: {
    id: string;
    sessionId?: string | null;
    itemId?: string | null;
    itemTitle?: string | null;
  };
}

interface ItemStanding {
  ranking: ItemRanking | null;
  listDecisionIds: ReadonlyMap<string, string>;
}

export function CallBehindProposal({ proposal }: CallBehindProposalProps) {
  const decisions = useDecisionsStore((s) => s.decisions);
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);
  const [decisionsReady, setDecisionsReady] = useState(decisions.length > 0);
  const [itemStanding, setItemStanding] = useState<ItemStanding | null>(null);

  useEffect(() => {
    if (decisions.length > 0) {
      setDecisionsReady(true);
      return;
    }
    let cancelled = false;
    void fetchDecisions().finally(() => {
      if (!cancelled) setDecisionsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [decisions.length, fetchDecisions]);

  // An item proposal: the item's rankings and the ids of the decisions its
  // agreed lists wrote, the way the item page reads them.
  const itemId = proposal.sessionId ? null : proposal.itemId ?? null;
  useEffect(() => {
    if (!itemId) {
      setItemStanding(null);
      return;
    }
    let cancelled = false;
    setItemStanding(null);
    fetchItemRankings(itemId)
      .then(async ({ rankings }) => {
        const listIds = rankings.map((r) => r.prioritySetId).filter((id): id is string => !!id);
        const listDecisionIds = listIds.length ? await fetchListApprovalDecisionIds(listIds).catch(() => new Map<string, string>()) : new Map<string, string>();
        if (!cancelled) setItemStanding({ ranking: pickRankingForTrail(rankings), listDecisionIds });
      })
      .catch(() => {
        if (!cancelled) setItemStanding({ ranking: null, listDecisionIds: new Map() });
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const incoming = useMemo(
    () => (proposal.sessionId ? decisionForIncomingList(proposal.sessionId, decisions) : null),
    [proposal.sessionId, decisions],
  );
  const standing = useMemo(
    () => (itemStanding?.ranking ? decisionForRanking(itemStanding.ranking, decisions, itemStanding.listDecisionIds) : null),
    [itemStanding, decisions],
  );

  if (!proposal.sessionId && !itemId) return null;

  const eyebrow = proposal.sessionId ? 'The call behind what you are about to accept' : 'The standing this proposal would change';
  const shell = (children: ReactNode) => (
    <section data-testid="call-behind-proposal" className="border border-[var(--rule)] dark:border-[var(--rule)] p-4">
      {children}
    </section>
  );
  const heading = <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">{eyebrow}</p>;
  const reading = <p className="mt-1 text-sm text-[var(--text-ter)]">Reading the decision record…</p>;

  if (!decisionsReady) return shell(<>{heading}{reading}</>);

  if (incoming) {
    if (incoming.decision) {
      return shell(
        <>
          <CallSentence compact eyebrow={eyebrow} decision={incoming.decision} decisions={decisions} />
          <p className="mt-2 text-xs text-[var(--text-ter)]">{incomingListBasisText(incoming.basis)}</p>
        </>,
      );
    }
    return shell(<>{heading}<p className="mt-1 text-sm text-[var(--text-sec)]">{incomingListGapText(incoming.gap ?? 'not_on_record')}</p></>);
  }

  // An item proposal.
  if (!itemStanding) return shell(<>{heading}{reading}</>);
  const ranking = itemStanding.ranking;
  const name = proposal.itemTitle?.trim() || 'This item';
  if (!ranking) {
    return shell(<>{heading}<p className="mt-1 text-sm text-[var(--text-sec)]">{name} holds no rank on the record yet. Accepting writes the first decision about it.</p></>);
  }
  const rankLabel = ranking.rank == null ? 'unranked' : `#${ranking.rank}${ranking.outOf ? ` of ${ranking.outOf}` : ''}`;
  const where = `${name} is ${rankLabel} on ${ranking.sourceName}`;
  if (standing?.decision) {
    return shell(
      <>
        <CallSentence compact eyebrow={eyebrow} decision={standing.decision} decisions={decisions} />
        <p className="mt-2 text-xs text-[var(--text-ter)]">{where}; read from the decision behind that rank.</p>
      </>,
    );
  }
  return shell(<>{heading}<p className="mt-1 text-sm text-[var(--text-sec)]">{where}. {rankingDecisionGapText(ranking, standing?.gap ?? 'not_on_record')}</p></>);
}
